/**
 * @fileoverview The share studio, for a plant, a location or a season: six
 * screens in a row, Pick, Layout, Size, Details, Crop and Share (owner,
 * 2026-10-15: "I think there should be one stable next button on the bottom
 * that allows you to page through the different options very quickly"; and
 * Crop the last before Share).
 *
 * Pick is a grid of the subject's photographs, newest first, tapped to
 * number them (the order the post goes out in, up to ten). The other five
 * are one frame that does not move: the top (close, the screen's name, "3
 * of 6", the six dashes), the finished picture at the screen's full width in
 * its own shape (no dark ground around it; a tall size, or a short window,
 * leaves STUDIO_BELOW_PICTURE under it), one band of choices under it that keeps its
 * height on every screen, and Back and "Next: Size" at the foot. Layout is
 * the layouts (and Card's ground); Size the four sizes and where each is for;
 * Details what is printed (name, variety, the date and its Day and Year,
 * the handle and which one) and the text size; Crop the photographs going
 * out, each tapped to move and zoom it in its frame (a tap on the picture
 * does the same on any screen); Share the words that go out with it, with
 * Save picture and Share at the foot. The arrows on the picture step between
 * the pictures going out, as a swipe does (no dots under it).
 *
 * No words are printed on the pictures any more (owner, 2026-10-15: "I think
 * we can drop the caption words on the image so that it takes up less
 * space"): the words go out with the post as its caption.
 *
 * Every picture it composes is posted with the link, so the page behind the
 * link shows what the gardener saw here, and nothing else.
 *
 * Share is plain (owner, 2026-10-12, after a design critique: "this looks
 * even more complicated… model it without the extra stuff"): the picture,
 * the words that go out with it shown under it, Share and Save picture.
 * The words are the caption starter the card hands over (captionStarter:
 * the plant's name, an emoji or two, a few hashtags), not edited here: the
 * gardener edits them where they post. They go with the share and the link,
 * and are copied too, as Instagram on Android drops shared text. No Copy
 * link, Copy picture, caption box or who-sees-it line.
 *
 * The share survives the phone (owner, 2026-10-12): everything the sheet
 * holds is kept on the device as it changes (shareDraft), so when Android
 * reloads Grow's tab while the gardener is in Instagram, the card opens the
 * sheet again (useResumeShare) on the same screen with the same photographs,
 * words and crops. A finished share leaves the sheet open on Share, to share
 * the same pictures somewhere else too; closing the sheet forgets the share.
 *
 * Where: PlantCard's Share, LocationCard's Share (a location subject, its
 * plants' photos given negative ids), ShareMoments, and the season review
 * (season: its card first, made by the page, then the season's plant photos,
 * their ids made negative, each framed as a plant's is).
 * API: POST /shares (the link, saved at its first use; a season's with season,
 * soFar, isPublic and layout 'season'), POST
 * /shares/:code/used, POST /shares/:code/frame/:i?count= (the pictures
 * behind the link); photo files through buildShareFrames.
 * Settings: the sharer's own choices, kept on the device (shareStyle);
 * the gardener's Grow address (GET /gardens/mine), the signature on the first picture; the account's social
 * links (the handle); SHARE_* tunables (config/tunables/plantList);
 * the server's limits.SHARE_MAX_PHOTOS (lib/growSettings).
 * Words (en/es): plants.share.* (steps.* (the six screens, nextTo, counter,
 * cropHint, cropNone, detailsHint), tools.crop, summary.*,
 * sizes.*, where.*, layouts.*, grounds.*, hints.*, el.*, phone.* (Move &
 * zoom), sameOnAll, collageWords, saved*, noPhotosYet*, and the rest),
 * sitewide.cancel, sitewide.close,
 * share.actions.* (Share, through ShareButtons, and Save picture),
 * locations.share.* (cover, on.*, el.*), locations.card.plants.count*,
 * seasonReview.sheet.card and seasonReview.public.on/off (a season's).
 * Watch out: photos under review are never offered. The words recorded with
 * the link (markShareUsed) are the one line for all, even when each picture
 * has its own.
 * Failures: a photograph the set could not fetch because of a failure on our
 * side says the standard sentence (D31, lib/serverFailure) with the support
 * address as a link; the link's own saves stay quiet but are reported.
 *
 * Since batch 18 (A105, 2026-10-19) this file is the shell: the props, the
 * frame and the preview. The rest is in features/share/studio, by job:
 * useShareStory (the story, the choices, the draft), useShareFrames (the
 * pictures made ahead, the preview's picture), useShareLink (the link),
 * usePreviewGestures (flicks, drags and taps on the preview), shareOut (Share
 * and Save picture), ShareStepBands (each screen's top and bands) and
 * MoveZoom; shareStudioTypes holds the shapes they share. SeasonShare and
 * SeasonCardChoices are passed on from here for the season review.
 *
 * @module SharePlantDialog
 */

import React from 'react';
import { Download, Check, X, ChevronLeft, ChevronRight, Maximize2, Crop } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/shared/components/ui/button';
import { Dialog, DialogContent, DialogDescription } from '@/components/ui/dialog';
import Spinner from '@/shared/components/ui/Spinner';
import { Pill } from '@/shared/components/ui/Pill';
import { PhotoButton } from '@/shared/components/ui/PhotoButton';
import { collageTiles } from '@/features/share/frames/frameLayouts';
import { SHARE_SAFE, SHARE_SIZES } from '@/features/share/frames/frameOptions';
import { SHARE_STEPS, type ShareStep } from '../../lib/shareDraft';
import { ShareActionButton } from '@/features/share/ShareButtons';
import { SHARE_BUTTON_CLASS } from '@/features/share/lib/shareLink';
import { FailureText } from '@/shared/components/ServerFailure';
import type { SharePlantDialogProps } from '@/features/share/studio/shareStudioTypes';
import { useShareStory } from '@/features/share/studio/useShareStory';
import { useShareFrames } from '@/features/share/studio/useShareFrames';
import { useShareLink } from '@/features/share/studio/useShareLink';
import { usePreviewGestures } from '@/features/share/studio/usePreviewGestures';
import { useShareOut } from '@/features/share/studio/shareOut';
import { CropBand, DetailsBand, LayoutBand, PickGrid, SizeBand, StepHeader } from '@/features/share/studio/ShareStepBands';
import { MoveZoom } from '@/features/share/studio/MoveZoom';
import { FramedPhoto } from '@/shared/components/ui/FramedPhoto';

export type { SeasonCardChoices, SeasonShare } from '@/features/share/studio/shareStudioTypes';

/** The room kept under the picture on the studio's screens, for the band of choices (two rows of chips) and the foot, so a Story or a Pin never pushes them off the screen. */
const STUDIO_BELOW_PICTURE = '12.5rem';

/**
 * Shares a plant's story as framed pictures: up to ten of its photographs,
 * in order.
 *
 * Six screens, none of which scrolls up and down: Pick, a grid of every
 * photograph the plant has, newest first, tapped to number them (it opens
 * with the one the card was showing picked); then Layout, Size, Details,
 * Crop and Share, one frame (the picture, a band of choices, Back and Next)
 * whose band alone changes.
 *
 * What is on the picture is the gardener's to decide, and they decide it by
 * looking: the preview is the actual frame, built by the same code that
 * builds what gets posted, and the picture can be dragged in its frame to
 * choose which part of it survives the crop.
 *
 * @param props - SharePlantDialogProps.
 */
export const SharePlantDialog: React.FC<SharePlantDialogProps> = (props) => {
  const { isOpen, variety, onAddPhoto, title } = props;
  /** The story, the screen and the sharer's choices (useShareStory). */
  const story = useShareStory(props);
  const {
    subject, closeSheet, t, ordered, picked, setSelection, shownPhoto, slides, step, setStep, shown, setShown,
    layout, set, order, collageIn, shownIsCollage, count, nothingToSend, style, error, saved, focusFor, saveFocus,
  } = story;
  /** The pictures, made ahead (useShareFrames). */
  const { buildError, progress, frames, failed, drawing, preparingShown, previewUrl, previewSrc, slotAspectOf } = useShareFrames(props, story);
  /** The link that goes with the share (useShareLink). */
  const { link, used } = useShareLink(props, story, frames);

  /** The chosen size in pixels, for the preview's shape. */
  const { w, h } = SHARE_SIZES[style.size];
  /** The preview under the finger, the full-size picture and Move & zoom (usePreviewGestures). */
  const {
    previewRef, nudge, enlarged, setEnlarged, editing, setEditing, canReposition, shownIndex, showStep,
    onPreviewDown, onPreviewMove, onPreviewUp, pinch,
  } = usePreviewGestures(isOpen, story, previewUrl, w, h, slotAspectOf);
  /** Share and Save picture (shareOut). */
  const { shareSheet, caption, handleShare, handleDownload } = useShareOut(props, story, frames, link, used);

  /** The edges an app's own buttons cover at this size. */
  const safe = SHARE_SAFE[style.size];
  /** Whether this size has edges an app covers, drawn on the preview. */
  const hasSafe = safe.top > 0 || safe.bottom > 0;

  /** A sharing failure, or the set for these choices could not be made. */
  const errorShown = error ?? (failed ? buildError?.message ?? null : null);

  /** Where this step stands among the six, for the dashes under the title and "3 of 6". */
  const stepIndex = SHARE_STEPS.indexOf(step);

  /*
   * The picture in view must be one going out. Choosing another layout can
   * leave it behind (the collage's slide once the collage is off, a
   * photograph tapped out on Pick), and the preview had nothing to show.
   * Before & after's first photograph stands for its one picture.
   */
  React.useEffect(() => {
    if (step === 'pick' || slides.length === 0 || shown === null) return;
    const inSlides = slides.some((p) => p.photo_id === shown) || (layout === 'ba' && order.includes(shown));
    if (!inSlides) setShown(slides[0].photo_id);
  }, [step, slides, shown, layout, order, setShown]);

  /**
   * Opens Move & zoom for the picture in view: its photograph at the frame's
   * shape, or in a collage its first tile's; null when the picture has no
   * photograph of its own to move (Before & after, a season's card).
   */
  const cropShown: (() => void) | null = shownIsCollage && collageIn && set[0]
    ? () => {
      const tiles = collageTiles(Math.min(9, set.length), w, h, w / 1080);
      if (tiles[0]) setEditing({ photo: set[0], aspect: tiles[0].w / tiles[0].h });
    }
    : canReposition && shownPhoto
      ? () => setEditing({ photo: shownPhoto, aspect: slotAspectOf(shownPhoto) })
      : null;
  /** The screen before this one, for Back. */
  const previousStep: ShareStep | undefined = SHARE_STEPS[stepIndex - 1];
  /** The screen after this one, which Next names and opens. */
  const nextStep: ShareStep | undefined = SHARE_STEPS[stepIndex + 1];
  /** Back, at the foot's left on every screen after Pick. */
  const backButton = previousStep ? (
    <Button type="button" variant="outline" onClick={() => setStep(previousStep)} className="h-12 shrink-0 gap-0.5 px-3 text-base">
      <ChevronLeft className="h-4 w-4" aria-hidden="true" />{t('plants.share.steps.back')}
    </Button>
  ) : null;
  /** The band under the picture on each screen after Pick: that screen's choices; on Share, the words that go out and how the share went. */
  const band = step === 'layout' ? <LayoutBand story={story} />
    : step === 'size' ? <SizeBand story={story} />
      : step === 'details' ? <DetailsBand story={story} />
        : step === 'crop' ? <CropBand story={story} slotAspectOf={slotAspectOf} setEditing={setEditing} />
          : (
            <div className="space-y-2">
              {/* The words that go out with it, as they go: to read, not to edit (the gardener edits them where they post). */}
              <p className="whitespace-pre-line text-sm leading-snug text-muted-foreground">{caption}</p>
              {errorShown && (
                <p className="flex items-center gap-1.5 text-sm text-destructive">
                  <X className="h-4 w-4 shrink-0" aria-hidden="true" /><span><FailureText text={errorShown} /></span>
                </p>
              )}
              {saved && (
                <p className="flex items-center gap-1.5 text-sm text-verdict-good" aria-live="polite">
                  <Check className="h-4 w-4 shrink-0" aria-hidden="true" />{saved}
                </p>
              )}
            </div>
          );
  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => !open && closeSheet()}>
      {/* Full screen on a phone; the same six screens in a wider window from
          sm up. The dialog's own close is hidden: each screen's top has its
          own. Escape still closes. */}
      <DialogContent
        scrollBody={false}
        // Escape closes the full-size picture first, then the sheet.
        onEscapeKeyDown={(event: KeyboardEvent) => { if (enlarged) { event.preventDefault(); setEnlarged(false); } }}
        className="flex h-[100dvh] max-h-none flex-col gap-0 overflow-hidden rounded-none border-0 p-0 max-sm:left-0 max-sm:top-0 max-sm:translate-x-0 sm:h-[min(92dvh,900px)] sm:max-h-[92dvh] sm:max-w-2xl sm:rounded-lg sm:border [&>button:last-child]:hidden"
      >
        <DialogDescription className="sr-only">{t('plants.share.promise')}</DialogDescription>

        {/* ------------------------------------------------------ 1 · Pick */}
        {step === 'pick' && (
          <>
            <StepHeader heading={title ?? t('plants.share.titleFor', { plant: variety })} step={step} closeSheet={closeSheet} t={t} />
            {ordered.length === 0 ? (
              // Nothing photographed yet: say what unlocks sharing, in the subject's own words, and offer it.
              <div className="flex flex-1 flex-col items-center justify-center gap-3 px-4 py-8 text-center">
                <p className="text-sm text-muted-foreground">{t(subject.kind === 'location' ? 'plants.share.noPhotosYetLocation' : 'plants.share.noPhotosYet')}</p>
                {onAddPhoto && (
                  <Button onClick={() => { closeSheet(); onAddPhoto(); }}>{t('plants.share.addPhoto')}</Button>
                )}
              </div>
            ) : (
              <>
                <PickGrid dialog={props} story={story} />
                <div className="shrink-0 border-t bg-background px-4 py-3">
                  <Button
                    className="h-12 w-full text-base"
                    disabled={picked.length === 0}
                    onClick={() => {
                      // The first picture going out is the one in view on Layout.
                      setSelection((current) => ({ ...current, shown: slides[0]?.photo_id ?? current.shown }));
                      setStep('layout');
                    }}
                  >
                    {picked.length === 1 ? t('plants.share.steps.nextOne') : t('plants.share.steps.nextCount', { count: picked.length })}
                  </Button>
                </div>
              </>
            )}
          </>
        )}

        {/* ------------------------------- 2–6 · Layout to Share, one frame */}
        {step !== 'pick' && (
          <>
            <StepHeader heading={t(`plants.share.steps.${step}`)} step={step} closeSheet={closeSheet} t={t} />
            {/*
              * The frame (owner, 2026-10-15): the picture at the full width in its
              * own shape, the band of choices under it, the foot. Only the band's
              * content changes from screen to screen, so nothing jumps. The
              * column is a size container (cqw, cqh): the picture is as tall as
              * its shape at the full width, but never so tall that the band and
              * the foot do not fit under it, so the screen never scrolls (owner,
              * 2026-10-15: "tighten vertically").
              */}
            <div className="flex min-h-0 flex-1 flex-col" style={{ containerType: 'size' }}>
              <div
                className="flex w-full shrink-0 items-center justify-center bg-muted/40"
                style={{ height: `min(${(h / w) * 100}cqw, 100cqh - ${STUDIO_BELOW_PICTURE})`, containerType: 'size' }}
              >
                <div
                  ref={previewRef}
                  onPointerDown={onPreviewDown}
                  onPointerMove={onPreviewMove}
                  onPointerUp={onPreviewUp}
                  onPointerCancel={onPreviewUp}
                  title={canReposition ? t('plants.share.repositionHint') : undefined}
                  className={cn(
                    'relative shrink-0 overflow-hidden', !previewSrc && 'bg-muted',
                    canReposition && previewSrc ? 'cursor-grab active:cursor-grabbing' : previewUrl && 'cursor-zoom-in',
                  )}
                  style={{ aspectRatio: `${w} / ${h}`, width: `min(100cqw, ${w / h} * 100cqh)`, touchAction: 'none' }}
                >
                  {previewSrc ? (
                    <FramedPhoto
                      src={previewSrc}
                      alt={t('plants.share.previewAlt')}
                      draggable={false}
                      className={cn('transition-opacity', drawing && 'opacity-75')}
                      style={{
                        ...(nudge ? { transform: `translate(${nudge.dx}px, ${nudge.dy}px)` } : {}),
                        // A pinch under way: the picture grows about the point the crop keeps.
                        ...(pinch ? { transform: `scale(${pinch.scale})`, transformOrigin: `${pinch.ox * 100}% ${pinch.oy * 100}%` } : {}),
                        ...(!previewUrl && shownPhoto ? { objectPosition: `${focusFor(shownPhoto).x * 100}% ${focusFor(shownPhoto).y * 100}%` } : {}),
                      }}
                    />
                  ) : (
                    <span className="absolute inset-0 flex items-center justify-center"><Spinner size={20} /></span>
                  )}
                  {/* Being made again for a while: said on the picture itself, at its top
                      (clear of the dots and the arrows), so nothing under the finger moves. */}
                  {preparingShown && previewSrc && (
                    <Pill
                      size="sm"
                      tone="photo"
                      role="status"
                      icon={<Spinner size={12} color="white" />}
                      className="pointer-events-none absolute left-1/2 top-2 z-10 -translate-x-1/2 shadow-sm backdrop-blur-sm"
                    >
                      {progress && progress.total > 1 && progress.done > 0 ? t('plants.share.preparingCount', progress) : t('plants.share.preparing')}
                    </Pill>
                  )}
                  {hasSafe && previewSrc && (
                    <>
                      <span className="pointer-events-none absolute inset-x-0 top-0 border-b border-dashed border-white/50 bg-black/30" style={{ height: `${safe.top * 100}%` }} aria-hidden="true" />
                      <span className="pointer-events-none absolute inset-x-0 bottom-0 border-t border-dashed border-white/50 bg-black/30" style={{ height: `${safe.bottom * 100}%` }} aria-hidden="true" />
                    </>
                  )}
                  {/* The finished picture at full size, behind its own button; a tap on the picture opens Move & zoom.
                      On Crop the button is the crop mark and opens Move & zoom for the picture in view
                      (owner, 2026-10-15: "make the expand function open the cropper"). */}
                  {step === 'crop' && cropShown ? (
                    <PhotoButton label={t('plants.share.tools.crop')} onClick={cropShown} className="absolute right-1.5 top-1.5 z-10">
                      <Crop className="h-4 w-4" aria-hidden="true" />
                    </PhotoButton>
                  ) : previewUrl && (
                    <PhotoButton label={t('plants.share.fullSize')} onClick={() => setEnlarged(true)} className="absolute right-1.5 top-1.5 z-10">
                      <Maximize2 className="h-4 w-4" aria-hidden="true" />
                    </PhotoButton>
                  )}
                  {/* Between the pictures going out: arrows on the picture, as a
                      swipe does; their presses are theirs, not the crop's drag.
                      No dots (owner, 2026-10-15): the arrows say there are more. */}
                  {count > 1 && previewSrc && (
                    <>
                      {shownIndex > 0 && (
                        <PhotoButton size="md" label={t('plants.share.previous')} onClick={() => showStep(-1)} className="absolute left-1.5 top-1/2 z-10 -translate-y-1/2">
                          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                        </PhotoButton>
                      )}
                      {shownIndex < count - 1 && (
                        <PhotoButton size="md" label={t('plants.share.next')} onClick={() => showStep(1)} className="absolute right-1.5 top-1/2 z-10 -translate-y-1/2">
                          <ChevronRight className="h-5 w-5" aria-hidden="true" />
                        </PhotoButton>
                      )}
                    </>
                  )}
                </div>
              </div>
              {/* The band: this screen's choices, the same room on every screen, scrolling inside itself if ever taller. */}
              <div className="min-h-0 flex-1 overflow-y-auto border-t bg-background px-4 py-3">
                {band}
              </div>
              {/* The foot: Back, then Next naming the screen it opens; on Share, Save picture and Share (wrapping when they do not fit). */}
              <div className="flex shrink-0 flex-wrap gap-2 border-t bg-background px-4 py-3">
                {backButton}
                {step === 'share' ? (
                  <>
                    {/*
                      * Save picture, and Share (ShareButtons' ShareActionButton) where
                      * the browser can share files. The words and the link travel in
                      * the share text and on the clipboard with either. Save picture
                      * always says its words (the audit, 2026-10-16: it had become an
                      * icon alone beside Share): beside Share it is the outline button
                      * at its own width, and the row wraps Share under it where the
                      * three do not fit; without Share, it is the wide button.
                      */}
                    <Button
                      variant={shareSheet ? 'outline' : 'default'}
                      className={cn(SHARE_BUTTON_CLASS, shareSheet ? 'h-12 shrink-0 px-3 text-[0.9375rem]' : 'h-12 min-w-0 flex-1 text-base')}
                      onClick={() => void handleDownload()}
                      disabled={!frames || nothingToSend}
                    >
                      {drawing ? (
                        <>
                          <Spinner size={16} />
                          <span className="ml-2 truncate">{progress && progress.total > 1 ? t('plants.share.preparingCount', progress) : t('plants.share.preparing')}</span>
                        </>
                      ) : (
                        <><Download className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" /><span className="truncate">{t('share.actions.savePicture')}</span></>
                      )}
                    </Button>
                    {shareSheet && (
                      <ShareActionButton className="h-12 min-w-[6rem] flex-1 text-base" onClick={() => void handleShare()} disabled={!frames || nothingToSend} />
                    )}
                  </>
                ) : nextStep && (
                  <Button className="h-12 min-w-0 flex-1 text-base" disabled={nothingToSend} onClick={() => setStep(nextStep)}>
                    <span className="truncate">{t('plants.share.steps.nextTo', { step: t(`plants.share.steps.${nextStep}`) })}</span>
                    <ChevronRight className="ml-1 h-4 w-4 shrink-0" aria-hidden="true" />
                  </Button>
                )}
              </div>
            </div>
          </>
        )}

        {/* The picture at full size, to read its words: the button on the picture opens it, a tap anywhere closes it. */}
        {enlarged && previewSrc && (
          <button
            type="button"
            onClick={() => setEnlarged(false)}
            aria-label={t('sitewide.close')}
            className="absolute inset-0 z-50 flex items-center justify-center bg-black/90 p-3"
          >
            <FramedPhoto fit="contain" src={previewSrc} alt={t('plants.share.previewAlt')} draggable={false} />
          </button>
        )}
      </DialogContent>
      {editing && (
        <MoveZoom
          src={editing.photo.signed_photo_url || editing.photo.cardUrl || editing.photo.thumbUrl || ''}
          aspect={editing.aspect}
          focus={focusFor(editing.photo)}
          onDone={(next) => {
            saveFocus(editing.photo, next, editing.aspect);
            setEditing(null);
          }}
          onClose={() => setEditing(null)}
        />
      )}
    </Dialog>
  );
};
